import {
  ArrowRight,
  CheckCircle2,
  Eye,
  EyeOff,
  KeyRound,
  LoaderCircle,
  LockKeyhole,
  ShieldCheck,
} from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link, Navigate, useLocation, useNavigate } from "../router";

import { ApiError } from "../api";
import { useAuth } from "../auth";
import { Button, InlineNotice } from "../components/ui";

export function AuthPage({ mode }: { mode: "login" | "register" }) {
  const { account, config, loading, login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!loading && account) {
    return <Navigate replace to="/dashboard" />;
  }

  const next = new URLSearchParams(location.search).get("next") || "/dashboard";

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === "register") {
        await register(displayName, email, password);
      } else {
        await login(email, password);
      }
      navigate(next.startsWith("/") && !next.startsWith("//") ? next : "/dashboard");
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Sign-in could not be completed. Try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-layout">
      <section className="auth-world">
        <div className="auth-world__top">
          <img
            alt="oCloud Studio"
            className="auth-studio-logo"
            src="/brand/ocloud-studio.webp"
          />
        </div>
        <div className="auth-world__content">
          <div className="pixel-cloud" aria-hidden="true">
            <span /><span /><span /><span /><span /><span /><span />
          </div>
          <p className="auth-kicker">Secure product access</p>
          <h1>Your licenses, ownership, and server slots in one clear place.</h1>
          <p>
            Sign in with the Discord account that received your key for automatic discovery,
            or use an oCloud account and attach a license securely.
          </p>
          <ul className="auth-promises">
            <li><ShieldCheck size={18} /> Discord remains the canonical owner</li>
            <li><KeyRound size={18} /> Recover and copy eligible license keys</li>
            <li><LockKeyhole size={18} /> Every account transfer needs owner approval</li>
          </ul>
        </div>
        <div className="auth-world__foot">oCloud Studio · Premium Minecraft software</div>
      </section>

      <main className="auth-panel">
        <div className="auth-panel__mobile-brand">
          <img alt="" src="/brand/ocloud-mark.png" />
          <strong>oCloud License</strong>
        </div>
        <div className="auth-form-wrap">
          <div className="auth-heading">
            <span className="auth-step">{mode === "login" ? "Welcome back" : "Create your workspace"}</span>
            <h2>{mode === "login" ? "Sign in to your licenses" : "Create an oCloud account"}</h2>
            <p>
              {mode === "login"
                ? "Use Discord for automatic license discovery, or enter your account credentials."
                : "After registration, add a license key from the dashboard. Its Discord owner remains in control."}
            </p>
          </div>

          {!config?.enabled && config?.message && <InlineNotice kind="warning">{config.message}</InlineNotice>}
          {error && <InlineNotice kind="danger">{error}</InlineNotice>}

          {config?.discordSignIn && (
            <a
              className="discord-button"
              href={`/v1/web/auth/discord?redirect=${encodeURIComponent(next)}`}
            >
              <DiscordMark />
              Continue with Discord
              <ArrowRight size={17} />
            </a>
          )}
          <div className="auth-divider"><span>or continue with email</span></div>

          <form className="auth-form" onSubmit={(event) => void submit(event)}>
            {mode === "register" && (
              <label>
                Display name
                <input
                  autoComplete="name"
                  maxLength={80}
                  minLength={2}
                  onChange={(event) => setDisplayName(event.target.value)}
                  placeholder="Your name or studio"
                  required
                  value={displayName}
                />
              </label>
            )}
            <label>
              Email address
              <input
                autoComplete="email"
                inputMode="email"
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@example.com"
                required
                type="email"
                value={email}
              />
            </label>
            <label>
              Password
              <span className="password-field">
                <input
                  autoComplete={mode === "login" ? "current-password" : "new-password"}
                  minLength={mode === "register" ? 12 : 1}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder={mode === "register" ? "12+ characters, letters and numbers" : "Enter your password"}
                  required
                  type={showPassword ? "text" : "password"}
                  value={password}
                />
                <button onClick={() => setShowPassword((value) => !value)} type="button">
                  {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                  <span className="sr-only">{showPassword ? "Hide password" : "Show password"}</span>
                </button>
              </span>
            </label>
            {mode === "register" && (
              <p className="password-hint"><CheckCircle2 size={15} /> Use a unique password you do not use elsewhere.</p>
            )}
            <Button disabled={busy || !config?.enabled} type="submit">
              {busy && <LoaderCircle className="spin" size={17} />}
              {mode === "login" ? "Sign in" : "Create account"}
              {!busy && <ArrowRight size={17} />}
            </Button>
          </form>

          <p className="auth-switch">
            {mode === "login" ? "New to oCloud License?" : "Already have an account?"}{" "}
            <Link to={mode === "login" ? "/register" : "/login"}>
              {mode === "login" ? "Create account" : "Sign in"}
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
}

function DiscordMark() {
  return (
    <svg aria-hidden="true" fill="currentColor" viewBox="0 0 24 24">
      <path d="M19.5 5.34A17.2 17.2 0 0 0 15.44 4l-.5 1.02a15.1 15.1 0 0 0-5.87 0L8.56 4A17.36 17.36 0 0 0 4.5 5.35C1.93 9.18 1.24 12.9 1.59 16.56a16.4 16.4 0 0 0 4.98 2.5l1.2-1.65c-.66-.25-1.29-.56-1.88-.93l.46-.36c3.63 1.68 7.56 1.68 11.15 0l.46.36c-.6.37-1.23.68-1.89.93l1.2 1.65a16.36 16.36 0 0 0 4.98-2.5c.42-4.24-.72-7.93-2.75-11.22ZM8.7 14.34c-1.09 0-1.98-1-1.98-2.22S7.6 9.9 8.7 9.9c1.1 0 2 1 1.98 2.22 0 1.23-.88 2.22-1.98 2.22Zm6.6 0c-1.09 0-1.98-1-1.98-2.22s.88-2.22 1.98-2.22c1.1 0 2 1 1.98 2.22 0 1.23-.88 2.22-1.98 2.22Z" />
    </svg>
  );
}
